import { takeLatest, select, put, call, all } from 'redux-saga/effects';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { updateShippingGroupsInfo, putServiceLevels } from 'yoda-interfaces/lib/Order/OrderApi';
import {
    getCustomerAddresses,
    getStores,
    deleteCustomerAddress,
    postCustomerAddress,
    updateCustomerAddress,
    addStore,
} from 'yoda-interfaces/lib/Account/AccountApi';
import { getCityStateByZipCode } from 'yoda-interfaces/lib/Zipcode/ZipcodeApi';
import { getStoreById } from 'yoda-interfaces/lib/Order/StoreApi';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import StoreAction from 'yoda-site-components/lib/actions/OrderAction';
import User from 'yoda-site-components/lib/helpers/User/User';
import sessionActions from 'yoda-site-components/lib/actions/SessionAction';
import * as findStoresActions from 'yoda-site-components/lib/actions/FindStoresAction';
import siteAnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import { CLEAR_SERVICE_ERROR, SET_SERVICE_ERROR } from 'yoda-site-components/lib/common/Constants';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { CLEAR_ORDER_ERROR } from 'yoda-checkout-components/lib/actionTypes/ServiceErrorHandlerActionTypes';
import * as CheckoutAnalyticsAction from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import {
    URL_CHECKOUT_PAYMENT,
    URL_REVIEW_PAGE,
    STREET_ERROR_MESSAGE,
} from 'yoda-checkout-components/lib/common/Constants';
import { handleCartExceptions } from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import { isSentToAgencyFromCart } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { scrollToElement } from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import {
    getAccertifyDeviceInfo,
    logExtendedAnalytics,
} from 'yoda-site-components/lib/sagas/SiteProbingSaga';
import { selectBnplSelected } from 'yoda-checkout-components/lib/selectors/PaymentMethodSelector';
import { selectPreferences } from 'yoda-site-components/lib/selectors/ContextSelector';
import * as newShippingActionTypes from '../actionTypes/NewShippingActionTypes';
import * as newShippingAction from '../actions/NewShippingAction';
import * as oldShippingAction from '../actions/ShippingAddressFormActions';
import * as CartActionTypes from '../actionTypes/CartActionTypes';
// eslint-disable-next-line import/no-cycle
import { initCheckoutSectionFromClientSideActions } from './NewCheckoutSaga';
// eslint-disable-next-line import/no-cycle
import CartDetailsSaga from './CartDetailsSaga';
import ShippingService from '../services/ShippingService';
import * as shippingSelector from '../selectors/NewShippingSelector';
import ApplicationLogger from '../helpers/ApplicationLogger';
import { getDAFormError } from '../helpers/Utils/Utils';
// eslint-disable-next-line import/no-cycle
import OrderDetailsModel from '../helpers/OrderDetailsModel';

const logger = ApplicationLogger.getLogger();
const contextSelector = (state) => state.context;
const orderDetailsSelector = (state) => state.orderDetails;
const newShippingSelector = (state) => state.newShipping;
const shipToHomeInfoSelector = (state) => state.newShipping.shipToHomeInfo;
const shipToStoreInfoSelector = (state) => state.newShipping.shipToStoreInfo;
const userInputAddressSelector = (state) => state.newShipping.shipToHomeInfo.userInputAddress;
const shippingMethodSelector = (state) => state.newShipping.shipToHomeInfo.shippingMethods;
const deleteShippingAddressInfoSelector = (state) =>
    state.newShipping.shipToHomeInfo.deleteShippingAddressInfo;
const standardizationNotRequiredSelector = (state) =>
    state.newShipping.shipToHomeInfo.xStandardizationNotRequired;
const userAddressListSelector = (state) => state.newShipping.shipToHomeInfo.userAddressList;
const pickupPersonInfoSelector = (state) => state.newShipping.shipToStoreInfo.pickupPersonInfo;
const pickupStoreListSelector = (state) => state.newShipping.shipToStoreInfo.pickUpStoreList;
const checkoutInfoSelector = (state) => state.checkoutInfo;
const defaultSelectedStore = (state) => state.selectedStore;
const isZipInvalidFromAPISelector = (state) =>
    get(state, 'newShipping.shipToHomeInfo.userInputAddress.isZipInvalidFromAPI');

function* updateOldCartDetailsSaga(pageName = 'delivery', nextPage) {
    yield put(showGlobalLoader(true));
    const payload = { includeServiceLevels: true, pageName };
    if (nextPage) {
        payload.nextPage = nextPage;
    }
    const action = {
        type: CartActionTypes.CART_DETAILS_GET_REQUEST,
        payload,
    };
    yield call(CartDetailsSaga, action);
}

/**
 * Fetch shipping address list from user profile
 * @return {IterableIterator<*>}
 */
export function* getCustomerAddress() {
    const context = yield select(contextSelector);
    const { headers } = context;
    const payload = ShippingService.getShippingAddressFromProfilePayload(headers, true);
    const result = yield call(FactorySaga, getCustomerAddresses, payload, logger);
    return result;
}

/**
 * Fetch store info based on store id
 * @param storeId
 * @return {IterableIterator<*>}
 */
function* getStoreInfo(storeId) {
    const storeInfo = {
        storeId,
    };
    yield put(showGlobalLoader(true));
    const result = yield call(getStoreById, storeInfo);
    yield put(showGlobalLoader(false));
    return result;
}

/**
 * Fetch store list form user profile
 * @return {IterableIterator<*>}
 */
function* fetchStoreListFromProfile() {
    const context = yield select(contextSelector);
    const { headers } = context;
    const { enablePreSourcing = true } = context?.featureFlags || {};
    const payload = {
        headers,
    };
    if (enablePreSourcing) {
        payload.payload = {
            xClientSource: 'CHECKOUT',
        };
    }
    const response = yield call(FactorySaga, getStores, payload);
    return response;
}

/**
 * Initialize ship to home section
 * @param orderDetailsInfo
 * @return {IterableIterator<*>}
 */
function* initShipToHomeSection(updateOrderInfoRequired) {
    yield put(showGlobalLoader(true));
    if (updateOrderInfoRequired) {
        yield call(updateOldCartDetailsSaga);
    }
    const orderDetailsInfo = yield select(orderDetailsSelector);
    let isUserLoggedIn = User.isUserLoggedIn();
    const checkoutInfo = yield select(checkoutInfoSelector);
    const { isLoadedFromReview } = checkoutInfo;
    const context = yield select(contextSelector);
    if (__SERVER__) {
        const cookies = get(context, 'headers.cookies');
        isUserLoggedIn = cookies.DP_USER_STATE === '1';
    }
    if (!isUserLoggedIn) {
        yield put(
            newShippingAction.initShippingSectionUI(
                isLoadedFromReview,
                orderDetailsInfo,
                undefined,
                context
            )
        );
    } else {
        const result = yield call(getCustomerAddress);
        const addressList = result.isSuccess && get(result.response, 'data.data', []);
        yield put(
            newShippingAction.initShippingSectionUI(
                isLoadedFromReview,
                orderDetailsInfo,
                addressList,
                context
            )
        );
    }
    yield put(showGlobalLoader(false));
}

/**
 * Initialize ship to store section
 * @param storeId
 * @return {IterableIterator<*>}
 */
function* initShipToStoreSection(allowAnalytics = true) {
    yield put(showGlobalLoader(true));
    const orderDetailsInfo = yield select(orderDetailsSelector);
    const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
    const sdpuInfo = yield select(shippingSelector.orderSdpuInfoSelector);
    const shippingInfoStore = yield select(newShippingSelector);
    let userStoreList = [];
    let isSelectedStoreExistInAccount = false;
    let storeId = get(shippingInfo, 'store.id') || get(sdpuInfo, 'store.id');
    if (!storeId) {
        const selectedStore = yield select(defaultSelectedStore);
        storeId = selectedStore.storeDetails && selectedStore.storeDetails.id;
    }
    let storeInfo = {
        storeId,
    };
    const checkoutInfo = yield select(checkoutInfoSelector);
    const { isLoadedFromReview } = checkoutInfo;
    let isUserLoggedIn = User.isUserLoggedIn();
    const context = yield select(contextSelector);
    if (__SERVER__) {
        const cookies = get(context, 'headers.cookies');
        isUserLoggedIn = cookies.DP_USER_STATE === '1';
    }
    if (isUserLoggedIn) {
        const result = yield call(fetchStoreListFromProfile);
        if (result.isSuccess) {
            userStoreList = get(result, 'response.data.data', []);
            isSelectedStoreExistInAccount = storeId && !!find(userStoreList, { id: storeId });
        }
    }
    if (!isSelectedStoreExistInAccount && storeId) {
        const result = yield call(getStoreInfo, storeId);
        const orderStoreInfo = get(result, 'data.stores[0]', {});
        storeInfo = ShippingService.getStoreInfoUI(orderStoreInfo);
    }
    yield put(
        newShippingAction.initShipToStoreSectionUI(
            orderDetailsInfo,
            storeInfo,
            userStoreList,
            isLoadedFromReview,
            isUserLoggedIn,
            true
        )
    );
    const shipStoreInfo = yield select(shipToStoreInfoSelector);
    const { pickUpStoreList } = shipStoreInfo || {};
    if (!storeInfo.storeId && (!pickUpStoreList || pickUpStoreList.length === 0)) {
        yield put(findStoresActions.openSlidePanel());
    }
    yield put(showGlobalLoader(false));

    /**
     * Fires DA event first time ship to store section is selected manually for the session
     */
    if (allowAnalytics && isEmpty(shippingInfoStore.shipToStoreInfo)) {
        yield put(CheckoutAnalyticsAction.toggleShippingTypeAnalyticsAction());
    }
}

/**
 * Init shipping section
 */
export default function* initNewShippingSection() {
    const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
    const shippingType = get(shippingInfo, 'type', 'HOME');
    if (shippingType === 'HOME') {
        yield call(initShipToHomeSection);
    } else {
        yield call(initShipToStoreSection, false);
    }
}

/**
 * Change shipping type
 * @return {IterableIterator<*>}
 */
function* changeShippingType(action) {
    yield put({ type: CLEAR_SERVICE_ERROR });
    // This action is to update old redux store. Remove this actions once redux changes went 100%
    yield put(oldShippingAction.changeAddressTypeAction(action.value));
    if (action.value === 'HOME') {
        yield call(initShipToHomeSection);
    } else {
        const selectedStore = yield select(defaultSelectedStore);
        if (!selectedStore.storeDetails) {
            yield put(StoreAction.getStoreByStoreId());
        }
        yield call(initShipToStoreSection, true);
    }
}
/**
 * Handle add credit card to order failed
 * @param error response
 */

function* addShippingAddressErrorHandler(result) {
    const errorCode =
        get(result, 'response.data[0].errorCode') || get(result, 'response.data.errorCode', '');
    if (
        errorCode === 'SRV_CUSTOMER_ADDRESS_ONE_INVALID' ||
        errorCode.indexOf('SRV_CUSTOMER_STD_CDI') !== -1
    ) {
        yield put(newShippingAction.setNewShippingAddressStandNotRequired(true));
    }
    if (errorCode === 'SRV_SESSION_TIMEOUT') {
        TokenProvider.logout();
        yield put(sessionActions.setSessionTimeOut(true));
    }
}

/**
 * Update shipping address into order
 * @param payload {should be sth or sts payload}
 */
function* updateShippingAddressToOrder(payload) {
    const result = yield call(FactorySaga, updateShippingGroupsInfo, payload);
    if (!result.isSuccess) {
        const data = get(result, 'response.data', []);
        handleCartExceptions(data);
    }
    return result;
}

function* buildADIDeviceInfoPayload(payload, actionName) {
    const context = yield select(contextSelector);
    const enableADIAccountUpdate = get(context, 'featureFlags.enableADIAccountUpdate', true);
    if (enableADIAccountUpdate) {
        const deviceInfo = yield call(getAccertifyDeviceInfo, {
            actionName,
            enableFeatureSpecificFlag: enableADIAccountUpdate,
        });
        if (deviceInfo) {
            payload.payload.params.deviceInfo = deviceInfo;
        }
    }
    return payload;
}
export function* handleADIResponseforAnalytics(result, actionMethod) {
    const context = yield select(contextSelector);
    const enableADIAccountUpdate = get(context, 'featureFlags.enableADIAccountUpdate', true);
    if (enableADIAccountUpdate) {
        const adiStatus = result?.response?.headers?.get('x-adi-sts');
        ['A', 'R'].includes(adiStatus)
            ? yield call(logExtendedAnalytics, {
                  result,
                  isTriggerFromLS: false,
                  actionMethod,
              })
            : '';
    }
}

/**
 * Add shipping address into user profile
 * @param payload
 */
function* addAddressToProfile(address) {
    const xStandardizationNotRequired = yield select(standardizationNotRequiredSelector);
    let payload = ShippingService.getAddShippingAddressToProfilePayload(
        address,
        xStandardizationNotRequired
    );
    payload = yield call(buildADIDeviceInfoPayload, payload, 'add_addressToProfileCCO');
    const result = yield call(FactorySaga, postCustomerAddress, payload);
    // Handle ADI response for analytics
    yield call(handleADIResponseforAnalytics, result, 'ADD_ADDRESS_PROFILE_CCO');
    return result;
}

/**
 * Update shipping address into user profile
 * @param payload
 */
function* updateAddressToProfile(address) {
    const xStandardizationNotRequired = yield select(standardizationNotRequiredSelector);
    let payload = ShippingService.getAddShippingAddressToProfilePayload(
        address,
        xStandardizationNotRequired
    );
    payload = yield call(buildADIDeviceInfoPayload, payload, 'update_addressToProfileCCO');
    const result = yield call(FactorySaga, updateCustomerAddress, payload);
    // Handle ADI response for analytics
    yield call(handleADIResponseforAnalytics, result, 'UPDATE_ADDRESS_PROFILE_CCO');
    return result;
}

/**
 * Update user selected shipping address into order
 * @param address
 */
function* changeShippingAddressToOrder(action) {
    let payload = {};
    const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
    const newShipping = yield select(newShippingSelector);
    const { skipShippingMethodSelections = false } = newShipping;
    const shippingMethodCode = skipShippingMethodSelections
        ? ShippingService.getUserSelectedShippingMethod(newShipping)
        : '';
    if (action.address.source.id) {
        /**
         * Add user profile address to order
         * @type {{payload}}
         */
        payload = ShippingService.getChangeShippingAddressToOrderPayload(
            shippingInfo.id,
            action.address.source.id,
            shippingMethodCode
        );
    } else {
        /**
         * Add transient address to order
         */
        payload = ShippingService.getUpdateShippingAddressToOrderPayload(
            shippingInfo.id,
            action.address.source,
            true,
            shippingMethodCode
        );
    }
    yield put(showGlobalLoader(true));
    yield all([
        put({ type: CLEAR_SERVICE_ERROR }),
        put({ type: CLEAR_ORDER_ERROR, payload: 'reviewShipping' }),
    ]);
    payload.payload.removeRestrictedItems = action.removeRestrictedItem;
    const result = yield call(updateShippingAddressToOrder, payload);
    const status = get(result, 'response.status', '');
    if (result.isSuccess) {
        yield call(updateOldCartDetailsSaga);
        const orderDetailsInfo = yield select(orderDetailsSelector);
        yield put(newShippingAction.changeShippingAddressUI(action.address, orderDetailsInfo));
    } else if (status === 207) {
        const addressId = get(
            payload,
            'payload.params.address.relationships.accountAddress.id',
            ''
        );
        if (addressId) {
            yield put({
                type: newShippingActionTypes.UPDATE_SELECTED_ADDRESS,
                addressId,
                hideDeliveryInfo: true,
            });
        }
        yield put(showGlobalLoader(false));
    } else {
        yield put(showGlobalLoader(false));
    }
}

/**
 * Submit ship to home address form
 */
function* submitSTHAddressForm(removeRestrictedItem) {
    const { showDeliveryOption = false } = removeRestrictedItem;
    const shipToHomeInfo = yield select(shipToHomeInfoSelector);
    /**
     * 1. Should add to selected address to order if register user address not saved to order scenario
     * 2. Guest user | address exist without service level | should add into order
     */
    if (!shipToHomeInfo.showAddShippingAddressForm) {
        const addressList = yield select(userAddressListSelector);
        let selectedAddress = find(addressList, { selected: true });
        if (isEmpty(selectedAddress)) {
            // eslint-disable-next-line prefer-destructuring
            selectedAddress = addressList[0];
        }
        if (selectedAddress) {
            const action = {
                address: selectedAddress,
                removeRestrictedItem,
            };
            yield call(changeShippingAddressToOrder, action);
        }
        return;
    }
    const userInputAddress = yield select(userInputAddressSelector);
    const isZipInvalidFromAPI = yield select(isZipInvalidFromAPISelector);
    const { address } = userInputAddress;
    const errorInfo = ShippingService.validateShippingAddressForm(address, [], isZipInvalidFromAPI);
    /**
     * Check for PO Box in fname, lname, company
     */
    const { firstName = '', lastName = '', company = '' } = address;
    const {
        poBoxPattern = ['po box', 'p o box', 'pobox', 'post office box', 'postoffice box'],
        poBoxErrorMessage = 'We cannot ship to PO Boxes',
    } = yield select(selectPreferences);
    if (ShippingService.isPatternPresent(firstName, poBoxPattern)) {
        errorInfo.firstName = poBoxErrorMessage;
    }
    if (ShippingService.isPatternPresent(lastName, poBoxPattern)) {
        errorInfo.lastName = poBoxErrorMessage;
    }
    if (ShippingService.isPatternPresent(company, poBoxPattern)) {
        errorInfo.company = poBoxErrorMessage;
    }
    /**
     * Check address line is valid or not from FE first time only
     */
    if (isEmpty(errorInfo) && !userInputAddress.isStreetAddressCheckExecuted) {
        const isNotValidStreetAddress = !ShippingService.isValidStreetAddress(
            address.lineOne,
            address.country
        );
        if (isNotValidStreetAddress) {
            errorInfo.lineOne = STREET_ERROR_MESSAGE;
        }
        yield put(newShippingAction.setStreetAddressCheckExecuted(true));
    }
    if (!isEmpty(errorInfo)) {
        /**
         * Setting Auto focus to first error fields
         */
        const errorKeys = Object.keys(errorInfo);
        const errorField = document.getElementById(errorKeys[0]);
        errorField && errorField.offsetParent && scrollToElement(errorField.offsetParent);

        /**
         * Set client side form errors into UI
         */
        yield all([
            put(siteAnalyticsAction.triggerFormError(getDAFormError(errorInfo))),
            put(newShippingAction.updateShippingAddressFormError(errorInfo)),
        ]);
    } else {
        yield put(newShippingAction.updateShippingAddressFormError({}));
        const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
        const xStandardizationNotRequired = yield select(standardizationNotRequiredSelector);
        const newShipping = yield select(newShippingSelector);
        const { skipShippingMethodSelections = false } = newShipping;
        const shippingMethodCode = skipShippingMethodSelections
            ? ShippingService.getUserSelectedShippingMethod(newShipping)
            : '';
        let payload = ShippingService.getUpdateShippingAddressToOrderPayload(
            shippingInfo.id,
            address,
            xStandardizationNotRequired,
            true,
            shippingMethodCode
        );
        const isUserLoggedIn = User.isUserLoggedIn();
        yield put(showGlobalLoader(true));
        yield put({ type: CLEAR_ORDER_ERROR, payload: 'reviewShipping' });
        if (isUserLoggedIn) {
            const profileAddressId = address.id;
            if (profileAddressId) {
                /**
                 * Update address to profile
                 */
                const updateAddressToProfileResult = yield call(updateAddressToProfile, address);
                if (updateAddressToProfileResult.isSuccess) {
                    payload = ShippingService.getChangeShippingAddressToOrderPayload(
                        shippingInfo.id,
                        profileAddressId,
                        shippingMethodCode
                    );
                } else if (
                    updateAddressToProfileResult.response.status === 400 ||
                    updateAddressToProfileResult.response.status === 401
                ) {
                    yield call(addShippingAddressErrorHandler, updateAddressToProfileResult);
                    yield put(showGlobalLoader(false));
                    return;
                }
            } else {
                /**
                 * Add address to profile
                 */
                const addAddressToProfileResult = yield call(addAddressToProfile, address);
                if (addAddressToProfileResult.isSuccess) {
                    const newAddressId = get(addAddressToProfileResult, 'response.data.id', '');
                    payload = ShippingService.getChangeShippingAddressToOrderPayload(
                        shippingInfo.id,
                        newAddressId,
                        shippingMethodCode
                    );
                } else if (
                    addAddressToProfileResult.response.status === 400 ||
                    addAddressToProfileResult.response.status === 401
                ) {
                    yield call(addShippingAddressErrorHandler, addAddressToProfileResult);
                    yield put(showGlobalLoader(false));
                    return;
                }
            }
        }
        /**
         * Add address to order
         */
        payload.payload.removeRestrictedItems = removeRestrictedItem;
        const result = yield call(updateShippingAddressToOrder, payload);
        const status = get(result, 'response.status', '');
        if (result.isSuccess) {
            if (skipShippingMethodSelections && !showDeliveryOption) {
                const request = {
                    pageName: URL_CHECKOUT_PAYMENT,
                };
                yield call(initCheckoutSectionFromClientSideActions, request);
            } else {
                yield call(initShipToHomeSection, true);
            }
        } else if (status === 207) {
            if (isUserLoggedIn) {
                const addressId = get(
                    payload,
                    'payload.params.address.relationships.accountAddress.id',
                    ''
                );
                yield call(initShipToHomeSection, true);
                if (addressId) {
                    yield put({
                        type: newShippingActionTypes.UPDATE_SELECTED_ADDRESS,
                        addressId,
                        hideDeliveryInfo: true,
                    });
                }
                const errorData = get(result, 'response.data', []);
                if (errorData[0]) {
                    const errorHandlerInfo = {
                        context: 'ADDRESS',
                        doNotClearToken: true,
                        errorComponent: 'SHIPPING_SECTION',
                    };
                    errorHandlerInfo.errorCode = errorData[0].errorCode || '';
                    errorHandlerInfo.errorMessage = errorData[0].errorMessage || '';
                    errorHandlerInfo.doNotClearToken = true;
                    errorHandlerInfo.errorDescription = errorData[0].errorDescription || '';
                    yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo });
                }
            }
            yield put(showGlobalLoader(false));
        } else {
            yield call(addShippingAddressErrorHandler, result);
            const relationshipsId = get(payload, 'params.address.relationships.accountAddress.id');
            if (relationshipsId) {
                yield call(initShipToHomeSection);
            }
            yield put(showGlobalLoader(false));
        }
    }
}
/**
 * Submit shipping section only on ship to home
 */
function* submitSTHShippingSelection() {
    const shippingMethods = yield select(shippingMethodSelector);
    const selectedShippingMethod = find(shippingMethods, { selected: true });
    const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
    if (selectedShippingMethod.code) {
        const shippingAddressOrderId = shippingInfo.id;
        const payload = ShippingService.getUpdateShippingMethodToOrderPayload(
            shippingAddressOrderId,
            selectedShippingMethod
        );
        yield put(showGlobalLoader(true));
        const result = yield call(FactorySaga, putServiceLevels, payload);
        yield put(showGlobalLoader(false));
        if (!result.isSuccess) {
            const data = get(result, 'response.data', []);
            handleCartExceptions(data);
            return;
        }
        yield call(initShipToHomeSection, true);
    }
}

/**
 * Submit shipping section
 */
function* submitShippingSection() {
    // yield put({ type: CLEAR_ORDER_ERROR, payload: 'reviewShipping' });
    const newShipping = yield select(newShippingSelector);
    let isShippingUpdated = false;
    if (newShipping.shippingType === 'HOME') {
        const { skipShippingMethodSelections = false } = newShipping;
        if (!skipShippingMethodSelections) {
            const shippingMethods = yield select(shippingMethodSelector);
            const selectedShippingMethod = find(shippingMethods, { selected: true });
            const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
            const orderShippingMethod = yield select(
                shippingSelector.orderSelectedShippingMethods
            ) || {};
            if (selectedShippingMethod.code !== orderShippingMethod.code) {
                const shippingAddressOrderId = shippingInfo.id;
                const payload = ShippingService.getUpdateShippingMethodToOrderPayload(
                    shippingAddressOrderId,
                    selectedShippingMethod
                );
                yield put(showGlobalLoader(true));
                const result = yield call(FactorySaga, putServiceLevels, payload);
                yield put(showGlobalLoader(false));
                if (!result.isSuccess) {
                    const data = get(result, 'response.data', []);
                    handleCartExceptions(data);
                    return;
                }
                isShippingUpdated = true;
            }
        }
    } else {
        const pickupStoreList = yield select(pickupStoreListSelector);
        const userInputPickupPersonInfo = yield select(pickupPersonInfoSelector);
        const pickupPersonInfo = { ...userInputPickupPersonInfo };
        const selectedStore = find(pickupStoreList, { selected: true });
        const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
        let isStoreChanged = true;
        let isPickupPersonInfoChanged = true;
        if (shippingInfo.type === 'STORE') {
            const orderStoreId = shippingInfo.store.id;
            const orderPickupPersonInfo = shippingInfo.pickup;
            isStoreChanged = orderStoreId !== selectedStore.storeId;
            isPickupPersonInfoChanged =
                !orderPickupPersonInfo ||
                ShippingService.isPickupPersonInfoChanged(pickupPersonInfo, orderPickupPersonInfo);
        }

        if (isStoreChanged || isPickupPersonInfoChanged) {
            const errorInfo = ShippingService.validateShipToStoreForm(pickupPersonInfo);
            if (!isEmpty(errorInfo)) {
                /**
                 * Set client side form errors into UI
                 */
                yield all([
                    put(newShippingAction.updateSTSFormError(errorInfo)),
                    put(siteAnalyticsAction.triggerFormError(getDAFormError(errorInfo))),
                ]);
                return;
            }
            const payload = ShippingService.getUpdateShipToStoreToOrderPayload(
                shippingInfo.id,
                selectedStore,
                pickupPersonInfo
            );
            yield put(showGlobalLoader(true));
            const result = yield call(updateShippingAddressToOrder, payload);
            yield put(showGlobalLoader(false));
            if (!result.isSuccess) {
                return;
            }
            isShippingUpdated = true;
        }
    }
    const checkoutInfo = yield select(checkoutInfoSelector);
    const { isLoadedFromReview } = checkoutInfo;
    if (isLoadedFromReview || isSentToAgencyFromCart(yield select(contextSelector))) {
        yield put(showGlobalLoader(true));
        yield call(updateOldCartDetailsSaga, 'review', URL_REVIEW_PAGE);
        return;
    }
    if (isShippingUpdated) {
        const request = {
            pageName: URL_CHECKOUT_PAYMENT,
        };
        yield call(initCheckoutSectionFromClientSideActions, request);
    } else {
        const cartDetailsResponse = yield select(orderDetailsSelector);
        const orderDetailsModel = new OrderDetailsModel(cartDetailsResponse);
        const initNewCheckoutSections = orderDetailsModel.initNewCheckoutSections(
            put,
            'payment',
            false,
            true
        );
        const { initCheckoutActions } = initNewCheckoutSections;
        const { initPageRenderActions } = initNewCheckoutSections;
        yield all(initCheckoutActions);
        yield call(initPageRenderActions);
        const checkoutURLConfig = {
            pathname: URL_CHECKOUT_PAYMENT,
        };
        getBrowserHistory().push(checkoutURLConfig);
        const context = yield select(contextSelector);
        const enableAddBnplPaymentCouponHeader = get(
            context,
            'featureFlags.enableAddBnplPaymentCouponHeader',
            true
        );
        const isBnplSelected = yield select(selectBnplSelected);
        const isAddBnplPaymentCouponHeader = enableAddBnplPaymentCouponHeader && isBnplSelected;
        // TODO need to re-investigate below call is required or not
        const payload = {
            includeServiceLevels: true,
            pageName: 'payment',
            isAddBnplPaymentCouponHeader,
        };
        yield put({ type: 'CART_DETAILS_GET_REQUEST', payload, adjustmentApplied: true });
    }
}

/**
 * Add store into user profile and preselect store
 * @param action
 * @return {IterableIterator<*>}
 */
function* addStoreToProfile(action) {
    const { storeId } = action;
    const pickupStoreList = yield select(pickupStoreListSelector);
    const storeExistInProfile = find(pickupStoreList, { storeId });
    if (storeExistInProfile) {
        if (!storeExistInProfile.selected) {
            yield put(newShippingAction.selectStoreFromProfile(storeId));
        }
        return;
    }
    yield put(showGlobalLoader(true));
    const context = yield select(contextSelector);
    const { enablePreSourcing = true } = context?.featureFlags || {};
    const payload = ShippingService.getAddStoreToProfilePayload(storeId);
    if (enablePreSourcing) {
        payload.payload.xClientSource = 'CHECKOUT';
    }
    const result = yield call(FactorySaga, addStore, payload);
    if (result.isSuccess) {
        const userStoreListResult = yield call(fetchStoreListFromProfile);
        if (userStoreListResult.isSuccess) {
            const userStoreList = userStoreListResult.response.data.data;
            yield put(newShippingAction.updateUserStoreListUI(storeId, userStoreList));
        }
    }
    yield put(showGlobalLoader(false));
}

/**
 * Update city and state based on zip code
 * @param action
 * @return {IterableIterator<*>}
 */
function* fetchCityStateByZip(action) {
    const request = {
        payload: action.zipCode,
    };
    const response = yield call(getCityStateByZipCode, request);
    const city = get(response, 'data.city');
    if (city) {
        const userInputAddress = yield select(userInputAddressSelector);
        if (!userInputAddress) return;
        const { address } = userInputAddress;
        if (address.country === 'US') {
            yield put(
                newShippingAction.updateShippingAddressFormValue(
                    'city',
                    startCase(city.toLowerCase())
                )
            );
        }
        yield put(newShippingAction.updateShippingAddressFormValue('state', response.data.state));
        const isEmptyForm = get(action, 'isEmptyForm', false);
        const newShipping = yield select(newShippingSelector);
        const { skipShippingMethodSelections = false } = newShipping;
        if (skipShippingMethodSelections && isEmptyForm) {
            const zipCode = get(action, 'zipCode');
            yield put(newShippingAction.updateShippingAddressFormValue('zip', zipCode));
        }
        yield put({ type: 'UPDATE_ZIP_ERROR', errorMessage: '' });
    } else {
        yield put({ type: 'UPDATE_ZIP_ERROR', errorMessage: 'Enter a valid ZIP code' });
    }
}

/**
 * Delete shipping address from user profile
 */
function* deleteShippingAddressFromProfile() {
    yield put({ type: CLEAR_ORDER_ERROR, payload: 'reviewShipping' });
    const deleteShippingAddressInfo = yield select(deleteShippingAddressInfoSelector);
    const { shippingAddressId } = deleteShippingAddressInfo;
    const payload = ShippingService.getDeleteShippingAddressFromProfilePayload(shippingAddressId);
    yield put(showGlobalLoader(true));
    const result = yield call(FactorySaga, deleteCustomerAddress, payload);
    if (result.isSuccess) {
        const shippingInfo = yield select(shippingSelector.orderShippingInfoSelector);
        const orderAccountAddressId = get(shippingInfo, 'address.relationships.accountAddress.id');
        /**
         * If order address deleted from user profile then default address should preselect.
         */
        if (orderAccountAddressId === shippingAddressId) {
            const userAddressList = yield select(userAddressListSelector);
            let defaultAddress = {};
            if (deleteShippingAddressInfo.isDefault) {
                defaultAddress = find(userAddressList, { source: { isDefault: true } });
            } else {
                defaultAddress = find(userAddressList, { showDefaultLabel: true });
            }
            const defaultAddressId = get(defaultAddress, 'source.id');
            if (defaultAddressId) {
                const changeAddressPayload = ShippingService.getChangeShippingAddressToOrderPayload(
                    shippingInfo.id,
                    defaultAddress.source.id
                );
                yield call(updateShippingAddressToOrder, changeAddressPayload);
            }
        }
        yield call(initShipToHomeSection, true);
    } else {
        yield put(showGlobalLoader(false));
    }
    yield put(newShippingAction.showDeleteShippingAddressWarning());
}

function* changeShippingAddressOnCancel(payload) {
    const userAddressList = yield select(userAddressListSelector);
    const selectedAddressId = (payload.address && payload.address.id) || '';
    let selectedAddress = find(userAddressList, { source: { id: selectedAddressId } });
    if (isEmpty(selectedAddress)) {
        selectedAddress = find(userAddressList, { selected: true });
    }
    const action = {
        address: selectedAddress,
    };
    yield call(changeShippingAddressToOrder, action);
}

function* removeRestrictedItemSaga(action) {
    yield call(submitSTHAddressForm, action.removeRestrictedItem);
}

function* changeShippingAddressSaga() {
    yield put({ type: CLEAR_SERVICE_ERROR });
    const userAddressList = yield select(userAddressListSelector);
    const selectedAddress = find(userAddressList, { selected: true });
    yield put(newShippingAction.editShippingAddress(selectedAddress));
}

function* changeStoreAddressSaga(storeDetails) {
    // clearing service when change to store with shipping service changes
    yield put({ type: CLEAR_SERVICE_ERROR });
    yield put(newShippingAction.changeSTSUpdateUI(storeDetails.storeInfo));
}

const watchInitNewShippingSection = function* watchInitNewShippingSection() {
    yield takeLatest(newShippingActionTypes.INIT_NEW_SHIPPING_SECTION, initNewShippingSection);
};
watchInitNewShippingSection.sagaName = 'watchInitNewShippingSection';

const watchSubmitNewSTHAddressForm = function* watchSubmitNewSTHAddressForm() {
    yield takeLatest(newShippingActionTypes.SUBMIT_NEW_SHIPPING_ADDRESS_FORM, submitSTHAddressForm);
};
watchSubmitNewSTHAddressForm.sagaName = 'watchSubmitNewSTHAddressForm';

const watchSubmitNewShippingSection = function* watchSubmitNewShippingSection() {
    yield takeLatest(newShippingActionTypes.SUBMIT_SHIPPING_SECTION, submitShippingSection);
};

watchSubmitNewShippingSection.sagaName = 'watchSubmitNewShippingSection';

const watchUpdateSTHShippingSelection = function* watchUpdateSTHShippingSelection() {
    yield takeLatest(
        newShippingActionTypes.SUBMIT_STH_SHIPPING_SELECTION,
        submitSTHShippingSelection
    );
};

watchUpdateSTHShippingSelection.sagaName = 'watchUpdateSTHShippingSelection';

const watchFetchCityStateByZip = function* watchFetchCityStateByZip() {
    yield takeLatest(newShippingActionTypes.FETCH_CITY_STATE_BY_ZIP, fetchCityStateByZip);
};
watchFetchCityStateByZip.sagaName = 'watchFetchCityStateByZip';

const watchDeleteShippingAddressFromProfile = function* watchDeleteShippingAddressFromProfile() {
    yield takeLatest(
        newShippingActionTypes.DELETE_SHIPPING_ADDRESS_FROM_PROFILE,
        deleteShippingAddressFromProfile
    );
};
watchDeleteShippingAddressFromProfile.sagaName = 'watchDeleteShippingAddressFromProfile';

const watchChangeShippingAddressRequest = function* watchChangeShippingAddressRequest() {
    yield takeLatest(
        newShippingActionTypes.CHANGE_SHIPPING_ADDRESS_REQUEST,
        changeShippingAddressToOrder
    );
};
watchChangeShippingAddressRequest.sagaName = 'watchChangeShippingAddressRequest';

const watchAddStoreToProfileRequest = function* watchAddStoreToProfileRequest() {
    yield takeLatest(newShippingActionTypes.NEW_ADD_STORE_TO_PROFILE, addStoreToProfile);
};
watchAddStoreToProfileRequest.sagaName = 'watchAddStoreToProfileRequest';

const watchUpdateShippingType = function* watchUpdateShippingType() {
    yield takeLatest(newShippingActionTypes.UPDATE_SHIPPING_TYPE, changeShippingType);
};
watchUpdateShippingType.sagaName = 'watchUpdateShippingType';

const watchChangeShippingAddressOnCancel = function* watchChangeShippingAddressOnCancel() {
    yield takeLatest(
        newShippingActionTypes.CHANGE_SHIPPING_ADDRESS_ON_CANCEL,
        changeShippingAddressOnCancel
    );
};
watchChangeShippingAddressOnCancel.sagaName = 'watchChangeShippingAddressOnCancel';

// to remove restricted item action will be trggred from service error component
const watchRemoveRestrictedItem = function* watchRemoveRestrictedItem() {
    yield takeLatest(newShippingActionTypes.REMOVE_RESTRICTED_ITEMS, removeRestrictedItemSaga);
};
watchRemoveRestrictedItem.sagaName = 'watchRemoveRestrictedItem';

// to open selectedaddress action will be triggered from service error component
const watchChangeShipingAddress = function* watchChangeShipingAddress() {
    yield takeLatest(newShippingActionTypes.CHANGE_SHIPPING_ADDRESS, changeShippingAddressSaga);
};
watchChangeShipingAddress.sagaName = 'watchChangeShipingAddress';

// to change a store action will be triggred
const watchChangeStoreAddress = function* watchChangeStoreAddress() {
    yield takeLatest(newShippingActionTypes.CHANGE_STORE_UPDATE_UI, changeStoreAddressSaga);
};
watchChangeStoreAddress.sagaName = 'watchChangeStoreAddress';

// get address list from profile to auto-populate BNPL fields
// const watchGetBnplAddress = function* watchGetBnplAddress() {
//     yield takeLatest(newShippingActionTypes.GET_BNPL_ADDRESS, initShipToHomeSection);
// };
// watchGetBnplAddress.sagaName = 'watchGetBnplAddress';

export {
    watchInitNewShippingSection,
    watchSubmitNewSTHAddressForm,
    watchSubmitNewShippingSection,
    watchFetchCityStateByZip,
    watchDeleteShippingAddressFromProfile,
    watchChangeShippingAddressRequest,
    watchAddStoreToProfileRequest,
    watchUpdateShippingType,
    watchChangeShippingAddressOnCancel,
    watchRemoveRestrictedItem,
    watchChangeShipingAddress,
    watchChangeStoreAddress,
    watchUpdateSTHShippingSelection,
};
